import React from 'react';

import { getIntlDate, trimStringMiddle } from '@cryptoboyio/mercuryo-lab/utils';
import { useTranslation } from 'react-i18next';

import { bemCssModules, getDisplayStatus, getTestId } from '@utils';
import { useTotalDigits } from '@utils/hooks';

import { TransactionsTypes, useStore } from '@modules';
import { TransactionTypesEnum } from '@modules/transactions/types';

import FeeInformer from '@components/FeeInformer';

import Icon from '../Icon';
import Amount from '../Pieces/Amount';
import Card from '../Pieces/Card';
import EmptyPage from '../Pieces/EmptyPage';
import Exchange from '../Pieces/Exchange';
import Hash from '../Pieces/Hash';
import MobilePay from '../Pieces/MobilePay';
import Source from '../Pieces/Source';
import Status from '../Pieces/Status';
import Time from '../Pieces/Time';
import TrxId from '../Pieces/TrxId';
import Type from '../Pieces/Type';
import getData from '../utils';

import style from './Table.scss';

const cn = bemCssModules(style, 'Table');
const ti = getTestId('Table');

export type TTable = {
  list: TransactionsTypes.TSortTransactions;
};

const Table: CustomFC<TTable> = ({ className, list }) => {
  const { t } = useTranslation();
  const [{ global, user }] = useStore();
  const { currencies } = global;
  const language = user.data?.language_code || CONST.i18n.languages.en.code;
  const { getTotalDigits, getDisplayDigits } = useTotalDigits();

  const getSource = (type: string): string => {
    switch (type) {
      case 'receipt':
        return t('transactions.with_mercuryo');
      case 'issue':
        return t('transactions.to_mercuryo');
      default:
        return '';
    }
  };

  return (
    <div className={cn(null, [className])} data-testid={ti()}>
      {Object.entries(list).length ? (
        Object.entries(list).map(([timestamp, value]) => {
          const date = getIntlDate(
            new Date(Number(timestamp)),
            {
              year: 'numeric',
              month: '2-digit',
              day: 'numeric',
            },
            language
          );

          return (
            <div key={timestamp}>
              <div className={cn('date')}>{date}</div>
              <table className={cn('table')} data-testid={ti('table')}>
                <tbody>
                  {Object.values(value).map((transaction: TransactionsTypes.TransactionType) => {
                    const {
                      type,
                      currency,
                      status,
                      created_at_ts,
                      mobile_pay,
                      tx,
                      trace,
                      id,
                      fee,
                      fee_currency,
                      fiat_amount,
                      fiat_currency,
                      amount,
                      card,
                    } = transaction;
                    const totalDigits = getTotalDigits(currency);
                    const displayDigits = getDisplayDigits(currency);
                    const displayStatus = getDisplayStatus(status);

                    const data = getData(transaction, totalDigits, displayDigits);
                    const source = getSource(type);
                    const mobilePaySystem = mobile_pay?.pay_system;

                    return (
                      <tr key={id}>
                        <td className={cn('icon-wrapper')}>
                          <Icon type={type} />
                        </td>
                        <td className={cn('info')} data-testid={ti('info')}>
                          <Type title={t(`transactions.${type}`)} type={type} testId={ti('type')} />
                          <Time timestamp={created_at_ts} data-testid={ti('time')} />

                          {tx?.url && tx?.id && (
                            <Hash
                              title={t('transactions.hash')}
                              url={tx.url}
                              id={tx.id}
                              testId={ti('hash')}
                            />
                          )}
                          {id && (
                            <TrxId
                              title={t('transactions.id')}
                              copy={trace?.trace_id || id}
                              id={trace?.trace_id ? trimStringMiddle(trace?.trace_id, 15) : id}
                              testId={ti('id')}
                            />
                          )}
                          <FeeInformer
                            amountSubtotal={data.total}
                            amountTotal={`${data.subtotal}`}
                            currency={data.currency}
                            fee={fee}
                            className={cn('informer')}
                            feeCurrency={fee_currency}
                            operationType={type}
                            currencies={currencies}
                            data-testid={ti('fee-informer')}
                          />
                        </td>
                        <td className={cn('amount-wrapper')}>
                          <span className={cn('currency')} data-testid={ti('currency')}>
                            {currency}
                          </span>
                          <Amount
                            status={status}
                            type={type}
                            amount={amount}
                            displayDigits={displayDigits}
                            totalDigits={totalDigits}
                            testId={ti('amount')}
                          />
                          {type === CONST.operations.exchange && (
                            <Exchange
                              currency={fiat_currency}
                              type={type}
                              amount={fiat_amount}
                              amountTestId={ti('fiat-amount')}
                              currencyTestId={ti('fiat-currency')}
                            />
                          )}
                          {card && (
                            <Card
                              title={t('transactions.by_card')}
                              cardNumber={card.number}
                              subTitle={t('transactions.by')}
                              paymentSystem={transaction.card.payment_system}
                              testId={ti('card')}
                            />
                          )}
                          {type === TransactionTypesEnum.mobile_pay && mobilePaySystem && (
                            <MobilePay mobilePaySystem={mobilePaySystem} />
                          )}
                          {status && (
                            <Status
                              status={status}
                              displayStatus={displayStatus}
                              testId={ti('status')}
                            />
                          )}
                          {source && <Source source={source} data-testid={ti('source')} />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })
      ) : (
        <EmptyPage title={t('transactions.no_transactions')} />
      )}
    </div>
  );
};

export default Table;
